import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, Subscription, combineLatest, map } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  SUPPORTED_UPLOAD_ACCEPT_TYPES,
  checkIfValidLibreOfficeType,
} from 'src/app/services/upload-file.service';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { CenterPosition } from 'src/app/services/items-canvas.service';
import { BulkUploadValidationService } from 'src/app/sessions/session/upload/bulk-upload-panel/services/bulk-upload-validation.service';
import { PdfManagerService } from 'src/app/sessions/session/pdf-canvas-item/pdf-services/pdf-manager.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PanelView } from '../../../../panel/panel.component';
import { SessionSharedDataService } from '../../../../../services/session-shared-data.service';
import { BulkUploadSubmitService } from './bulk-upload-submit.service';

const BULK_UPLOAD_ERROR_EVENT = 'Bulk Upload Error';

export const MAX_BULK_UPLOAD_FILES = 20;

export enum BulkUploadStep {
  Drop = 1,
  Processing = 2,
  PageSelection = 3,
  InsertStrategy = 4,
  Arrangement = 5,
}

export enum BulkUploadFileStatus {
  Idle = 'idle',
  Processing = 'processing',
  Ready = 'ready',
  Error = 'error',
}

export interface BulkUploadFile {
  id: string;
  contentId: string;
  file: File;
  name: string;
  mimeType: string;
  isPdf: boolean;
  pages: number[];
  pagePreviews: string[];
  pageDimensions: Array<{ width: number; height: number }>;
  selectedPageIndices?: number[];
  status: BulkUploadFileStatus;
  progress: number;
  errorMessage?: string;
}

export interface BulkUploadConfig {
  maxFiles: number;
  supportedTypes: string;
}

export interface BulkUploadSelectionState {
  selectedFileIds: string[];
  selectedPagesIndicesByFileId: Record<string, number[]>;
}

interface ImmediateUploadPayload {
  files: File[];
  position?: CenterPosition;
}
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
/**
 * Central state manager for the bulk upload flow.
 * Handles validation, file partitioning, PDF conversion, progress tracking,
 * and step transitions for the upload UI.
 */
export class BulkUploadService {
  readonly currentStep$ = new BehaviorSubject<BulkUploadStep>(BulkUploadStep.Drop);
  readonly files$ = new BehaviorSubject<BulkUploadFile[]>([]);
  readonly errorMessage$ = new BehaviorSubject<string | undefined>(undefined);
  readonly uploadedFilesContainPdfs$ = new BehaviorSubject<boolean>(false);
  readonly selectedSingleNonPdfFile$ = this.files$.pipe(
    map((files) => {
      if (files.length !== 1) {
        return undefined;
      }
      const file = files[0];
      return file.isPdf ? undefined : file;
    }),
  );
  readonly config: BulkUploadConfig = {
    maxFiles: MAX_BULK_UPLOAD_FILES,
    supportedTypes: SUPPORTED_UPLOAD_ACCEPT_TYPES,
  };

  // Files to upload instantly once the user select them, no need for insertion nor arrangement strategy selection
  readonly immediateUploadFiles$ = new Subject<ImmediateUploadPayload>();

  private readonly progressSubscriptions = new Map<string, Subscription>();

  readonly overallProgress$ = this.files$.pipe(
    map((files) => {
      if (!files.length) {
        return 0;
      }
      const total = files.reduce((sum, file) => sum + (file.progress ?? 0), 0);
      return Math.round(total / files.length);
    }),
  );

  readonly selectedPagesCount$ = this.files$.pipe(
    map((files) => files.reduce((sum, file) => sum + this.getSelectedPageCount(file), 0)),
  );

  readonly selectedFilesCount$ = this.files$.pipe(
    map((files) => files.filter((file) => this.getSelectedPageCount(file) > 0).length),
  );
  readonly isBulkUploadActive$ = combineLatest([
    this.flagsService.featureFlagChanged(FLAGS.ENABLE_BULK_UPLOAD_FLOW),
    this.currentStep$,
  ]).pipe(map(([isEnabled, step]) => isEnabled && step !== BulkUploadStep.Drop));

  constructor(
    private readonly validationService: BulkUploadValidationService,
    private readonly pdfManagerService: PdfManagerService,
    private readonly translateService: TranslateService,
    private readonly sharedDataService: SessionSharedDataService,
    private readonly flagsService: FlagsService,
    private readonly bulkUploadSubmitService: BulkUploadSubmitService,
    private readonly telemetryService: TelemetryService,
  ) {
    // Immediate uploads (non-PDFs) are submitted as soon as they are selected.
    this.immediateUploadFiles$.pipe(untilDestroyed(this)).subscribe(({ files, position }) => {
      this.bulkUploadSubmitService.submitImmediateFiles(files, position);
      if (!this.uploadedFilesContainPdfs$.getValue()) {
        this.resetUploadFlow();
        this.sharedDataService.changeLeftPanelView.next(undefined);
      }
    });
  }

  addFiles(files: File[], position?: CenterPosition) {
    // validating selected files before uploading them
    this.errorMessage$.next(undefined);
    const errorMessage = this.validationService.validateFiles(
      files,
      this.config.maxFiles,
      this.config.supportedTypes,
    );
    if (errorMessage) {
      this.openLeftPanelIfNotOpened(); // to show the error message
      this.resetUploadFlow(false, errorMessage);
      return;
    }

    // Splitting the files to PDF (including libero office files) and non PDFS (images, videos..), to direct each to a dedicated stage.
    const pdfFiles = files.filter((file) => this.isPdfFileByType(file));
    const nonPdfFiles = files.filter((file) => !this.isPdfFileByType(file));

    const uploadedFilesContainPDFs = pdfFiles.length > 0;
    this.uploadedFilesContainPdfs$.next(uploadedFilesContainPDFs);

    if (nonPdfFiles.length) {
      this.immediateUploadFiles$.next({ files: nonPdfFiles, position });
    }

    if (!uploadedFilesContainPDFs) {
      this.resetUploadFlow(false);
      return;
    }

    const nextFiles = pdfFiles.map((file, index) => ({
      id: `${file.name}-${index}`,
      contentId: uuidv4(),
      file,
      name: file.name,
      mimeType: file.type,
      isPdf: true,
      pages: [],
      pagePreviews: [],
      pageDimensions: [],
      status: BulkUploadFileStatus.Processing,
      progress: 0,
    }));

    // Removing the immediate files as they were uploaded now and the flow should continue with PDFs only
    this.files$.next(nextFiles);
    this.currentStep$.next(BulkUploadStep.Processing);
    this.openLeftPanelIfNotOpened();
    this.processPdfFiles(nextFiles);
  }

  resetUploadFlow(closePanel = true, errorMessage?: string) {
    this.files$.next([]);
    this.currentStep$.next(BulkUploadStep.Drop);
    this.errorMessage$.next(errorMessage);
    if (closePanel) {
      this.uploadedFilesContainPdfs$.next(false);
    }
  }

  applySelections(selection: BulkUploadSelectionState) {
    const selectedIds = new Set(selection.selectedFileIds);
    const selectedPageIndicesByFileId = selection.selectedPagesIndicesByFileId;

    const nextFiles = this.files$.getValue().map((file) => {
      if (!file.isPdf) {
        return file;
      }

      const selectedIndices = selectedIds.has(file.id)
        ? selectedPageIndicesByFileId[file.id] ?? []
        : [];

      return {
        ...file,
        selectedPageIndices: selectedIndices,
      };
    });

    this.files$.next(nextFiles);
  }

  proceedToNextStep() {
    const currentStep = this.currentStep$.getValue();
    const nextStep = this.resolveNextStep(currentStep);
    if (nextStep) {
      this.currentStep$.next(nextStep);
    }
  }

  backStep() {
    const currentStep = this.currentStep$.getValue();
    const previousStep = this.resolvePreviousStep(currentStep);
    if (previousStep) {
      this.currentStep$.next(previousStep);
    }
  }

  private resolveNextStep(currentStep: BulkUploadStep): BulkUploadStep | undefined {
    if (currentStep === BulkUploadStep.Drop) {
      const fileSummary = this.getUploadedFilesSummary();
      if (fileSummary.singleNonPdf) {
        return undefined; // will insert instantly and reset the upload
      }
      return BulkUploadStep.Processing;
    }

    if (currentStep === BulkUploadStep.Processing) {
      return BulkUploadStep.PageSelection;
    }

    if (currentStep === BulkUploadStep.PageSelection) {
      return BulkUploadStep.InsertStrategy;
    }

    if (currentStep === BulkUploadStep.InsertStrategy) {
      const fileSummary = this.getUploadedFilesSummary();
      if (fileSummary.singleFile) {
        return undefined; // upload instantly nothing to arrange
      }
      return BulkUploadStep.Arrangement;
    }

    return undefined;
  }

  private resolvePreviousStep(currentStep: BulkUploadStep): BulkUploadStep | undefined {
    if (currentStep === BulkUploadStep.Processing || currentStep === BulkUploadStep.PageSelection) {
      return BulkUploadStep.Drop;
    }

    if (currentStep === BulkUploadStep.InsertStrategy) {
      return BulkUploadStep.PageSelection;
    }

    if (currentStep === BulkUploadStep.Arrangement) {
      return BulkUploadStep.InsertStrategy;
    }

    return undefined;
  }

  private getSelectedPageCount(file: BulkUploadFile): number {
    if (file.selectedPageIndices) {
      return file.selectedPageIndices.length;
    }
    return file.pages?.length ?? 0;
  }

  private getUploadedFilesSummary() {
    const files = this.files$.getValue();
    const selectedFiles = files.filter((file) => this.getSelectedPageCount(file) > 0);
    const fileCount = selectedFiles.length;
    const pdfCount = selectedFiles.filter((file) => file.isPdf).length;
    return {
      fileCount,
      singleFile: fileCount === 1,
      multipleFiles: fileCount > 1,
      singlePdf: fileCount === 1 && pdfCount === 1,
      singleNonPdf: fileCount === 1 && pdfCount === 0,
    };
  }

  private isPdfFileByType(file: File) {
    return (
      file.type.toLowerCase().includes('application/pdf') ||
      file.name.toLowerCase().endsWith('.pdf') ||
      checkIfValidLibreOfficeType(file) // as it will be converter to PDF using the to-images converter
    );
  }

  private async processPdfFiles(files: BulkUploadFile[]) {
    let completedCount = 0;
    let successfulCount = 0;
    let failedCount = 0;

    const finalizeProcessingIfDone = () => {
      completedCount += 1;
      if (completedCount !== files.length) {
        return;
      }

      if (successfulCount === 0 && failedCount > 0) {
        const message =
          failedCount === 1
            ? this.translateService.instant('File could not be processed')
            : this.translateService.instant('All {{count}} files could not be processed', {
                count: failedCount,
              });

        this.logBulkUploadError('all_files_failed', {
          userMessage: message,
          failedCount,
          files: this.files$.getValue().map((file) => this.getBulkUploadFileLogContext(file)),
        });
        this.resetUploadFlow(false, message);
        return;
      }

      if (successfulCount > 0 && this.currentStep$.getValue() === BulkUploadStep.Processing) {
        this.proceedToNextStep();
      }
    };

    const tasks = files.map(async (file) => {
      try {
        const result = await this.processPDF(file);
        this.updateFileState(file.id, result);
        if (result.status === BulkUploadFileStatus.Error) {
          failedCount += 1;
        } else {
          successfulCount += 1;
        }
        return result;
      } catch (error) {
        failedCount += 1;
        this.logBulkUploadError('unexpected_processing_error', {
          stage: 'processPdfFiles',
          ...this.getBulkUploadFileLogContext(file),
          ...this.serializeError(error),
        });
        const failedResult: BulkUploadFile = {
          ...file,
          status: BulkUploadFileStatus.Error,
          errorMessage: 'Failed to process file',
          progress: 100,
        };
        this.updateFileState(file.id, failedResult);
        return failedResult;
      } finally {
        finalizeProcessingIfDone();
      }
    });

    await Promise.all(tasks);
  }

  private async processPDF(file: BulkUploadFile): Promise<BulkUploadFile> {
    try {
      this.trackPdfProgress(file.id, file.contentId);

      const processed = await this.pdfManagerService.uploadPdfAsImages(file.file, file.contentId);

      if (!processed) {
        this.logBulkUploadError('pdf_upload_failed', {
          stage: 'uploadPdfAsImages',
          ...this.getBulkUploadFileLogContext(file),
        });
        return {
          ...file,
          status: BulkUploadFileStatus.Error,
          errorMessage: 'Failed to upload file',
          progress: 100,
        };
      }

      const conversionResponse = await this.pdfManagerService.getConvertedImages(file.contentId);
      if (!conversionResponse) {
        this.logBulkUploadError('pdf_conversion_result_missing', {
          stage: 'getConvertedImages',
          ...this.getBulkUploadFileLogContext(file),
        });
        return {
          ...file,
          status: BulkUploadFileStatus.Error,
          errorMessage: 'Failed to process file',
          progress: 100,
        };
      }

      const pages = Array.from(
        { length: conversionResponse.numberOfPages },
        (_, index) => index + 1,
      );
      const selectedPageIndices = pages.map((_, index) => index);

      const pageDimensions = this.pdfManagerService.decompressImagesSizes(conversionResponse);
      const pagePreviews = Array.from({ length: conversionResponse.numberOfPages }, (_, index) =>
        this.pdfManagerService.getImageUrl(
          index,
          conversionResponse.imagesPrefix,
          conversionResponse.imagesToken,
        ),
      );
      return {
        ...file,
        pages,
        pagePreviews,
        pageDimensions,
        selectedPageIndices,
        status: BulkUploadFileStatus.Ready,
        progress: 100,
      };
    } catch (error) {
      this.logBulkUploadError('pdf_processing_exception', {
        stage: 'processPDF',
        ...this.getBulkUploadFileLogContext(file),
        ...this.serializeError(error),
      });
      return {
        ...file,
        status: BulkUploadFileStatus.Error,
        errorMessage: 'Failed to process file',
        progress: 100,
      };
    } finally {
      this.clearPdfProgress(file.id);
    }
  }

  /**
   * Logs structured bulk-upload failures to FullStory for session troubleshooting.
   * Creates both a searchable `[Error] Bulk Upload Error` event and a console log line.
   */
  private logBulkUploadError(reason: string, details: Record<string, unknown> = {}): void {
    const fileName = typeof details.fileName === 'string' ? details.fileName : undefined;
    const consoleMessage = fileName
      ? `[Bulk Upload] ${reason}: ${fileName}`
      : `[Bulk Upload] ${reason}`;

    this.telemetryService.errorEvent(BULK_UPLOAD_ERROR_EVENT, {
      reason,
      ...details,
    });
    this.telemetryService.log('error', consoleMessage);
  }

  private getBulkUploadFileLogContext(file: BulkUploadFile): Record<string, unknown> {
    return {
      fileId: file.id,
      contentId: file.contentId,
      fileName: file.name,
      mimeType: file.mimeType || file.file.type || 'unknown',
      fileSizeBytes: file.file.size,
      status: file.status,
      errorMessage: file.errorMessage,
    };
  }

  private serializeError(error: unknown): { errorMessage: string; errorName?: string } {
    if (error instanceof Error) {
      return {
        errorMessage: error.message,
        errorName: error.name,
      };
    }
    return { errorMessage: String(error) };
  }

  private updateFileState(fileId: string, updates: Partial<BulkUploadFile>) {
    const files = this.files$.getValue();
    const nextFiles = files.map((file) => (file.id === fileId ? { ...file, ...updates } : file));
    this.files$.next(nextFiles);
  }

  private trackPdfProgress(fileId: string, contentId: string) {
    this.clearPdfProgress(fileId);
    const sub = this.pdfManagerService.getPdfUploadProgress(contentId).subscribe((progress) => {
      this.updateFileState(fileId, { progress: Math.round(progress) });
    });
    this.progressSubscriptions.set(fileId, sub);
  }

  private clearPdfProgress(fileId: string) {
    const sub = this.progressSubscriptions.get(fileId);
    if (sub) {
      sub.unsubscribe();
      this.progressSubscriptions.delete(fileId);
    }
  }

  private openLeftPanelIfNotOpened() {
    if (this.sharedDataService.leftPanelView.getValue()?.panelView !== PanelView.uploadFile) {
      // only call this when the value is not already PanelView.uploadFile because if so, the panel will close
      this.sharedDataService.changeLeftPanelView.next(PanelView.uploadFile);
    }
  }
}
